import pdfMake from 'pdfmake'
import { useEffect, useMemo, useState } from 'react'
import {
  ButtonGroup,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from 'react-bootstrap'
import {
  ArrowRepeat,
  BoxArrowUpRight,
  FiletypeXlsx,
  PencilFill,
  PlusCircleFill,
  PrinterFill,
  Search,
  TrashFill,
} from 'react-bootstrap-icons'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { CustomButton } from '../../components/CustomButton'
import { CustomSpinner } from '../../components/CustomSpinner'
import Navbar from '../../components/navbar/navbar'
import ReclamacoesService from '../../services/database/ReclamacoesService'
import { formatDate } from '../../utils/formatDate'
import { formatPrice } from '../../utils/formatPrice'
import { sendErrorApiMessage } from '../../utils/sendErrorApiMessage'
import { ModalCreate } from './ModalCreate'
import { ModalEdit } from './ModalEdit'
import { useOrdenation } from '../../hooks/useOrdenation'
import { ContentLoading } from '../../components/ContentLoading'
import { useNavigate } from 'react-router-dom'
import { CustomInputDate } from '../../components/CustomInputDate'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { format } from 'date-fns'

export function Reclamacoes() {
  const [reclamacoes, setReclamacoes] = useState([])
  const [status, setStatus] = useState([])
  const [selectedStatus, setSelectedStatus] = useState('')
  const [cliente, setCliente] = useState('')
  const [selectedClaimCode, setSelectedClaimCode] = useState('')
  const [isRequestLoading, setIsRequestLoading] = useState(true)
  const [isStatusLoading, setStatusLoading] = useState(true)
  const [isSyncLoading, setIsSyncLoading] = useState(false)
  const [isDeleteRequestLoading, setIsDeleteRequestLoading] = useState({})
  const [isModalCreateOpen, setIsModalCreateOpen] = useState(false)
  const [isModalEditOpen, setIsModalEditOpen] = useState(false)
  const [isMoreActionsOpen, setIsMoreActionsOpen] = useState({})
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState(new Date())
  const [selectedAccount, setSelectedAccount] = useState('')

  const navigate = useNavigate()

  const searchedItems = useMemo(() => {
    let data = reclamacoes

    if (cliente) {
      data = data.filter((reclamacao) =>
        String(reclamacao.cliente)
          .toLowerCase()
          .includes(String(cliente).toLowerCase()),
      )
    }

    if (selectedStatus) {
      data = data.filter((reclamacao) => reclamacao.status === selectedStatus)
    }

    if (selectedAccount) {
      data = data.filter((reclamacao) => reclamacao.apelido === selectedAccount)
    }

    return data
  }, [cliente, reclamacoes, selectedStatus, selectedAccount])

  const { filteredItems, Th, Paginate, itemsPerPage, setItemsPerPage } =
    useOrdenation(searchedItems, 'data_entrada', 'desc', 50)

  async function loadStatus() {
    try {
      setStatusLoading(true)

      const data = await ReclamacoesService.getStatus()
      setStatus(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível carregar o status!')
    } finally {
      setStatusLoading(false)
    }
  }

  async function loadReclamacoes(startDate, endDate) {
    try {
      setIsRequestLoading(true)

      const data = await ReclamacoesService.getAll(startDate, endDate)

      setReclamacoes(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível carregar as reclamações!')
    } finally {
      setIsRequestLoading(false)
    }
  }

  async function handleDelete(code, index) {
    try {
      setIsDeleteRequestLoading((prevState) => ({
        ...prevState,
        [index]: true,
      }))

      await ReclamacoesService.delete(code)
      loadReclamacoes()
      toast.success('Reclamação deletada com sucesso!')
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível deletar a reclamação!')
    } finally {
      setIsDeleteRequestLoading((prevState) => ({
        ...prevState,
        [index]: false,
      }))
    }
  }

  async function handleAskDelete(code, index) {
    Swal.fire({
      title: `Deseja deletar a reclamação #${code}?`,
      showDenyButton: true,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      denyButtonText: `Deletar`,
      showConfirmButton: false,
    }).then((result) => {
      if (result.isDenied) {
        handleDelete(code, index)
      }
    })
  }

  function exportToExcel() {
    const reclamacoesFormatted = filteredItems.map((reclamacao) => ({
      id: reclamacao.codigo,
      cliente: reclamacao.cliente,
      pedido: reclamacao.numero_pedido,
      data_entrada: format(new Date(reclamacao.data_entrada), 'dd/MM/yyyy'),
      conta: reclamacao.apelido,
      status: reclamacao.status,
    }))

    // ordem das colunas
    const headers = [
      'id',
      'cliente',
      'pedido',
      'data_entrada',
      'conta',
      'status',
    ]

    const worksheet = XLSX.utils.json_to_sheet(reclamacoesFormatted, {
      header: headers,
    })

    // 👇 renomeia títulos visíveis
    XLSX.utils.sheet_add_aoa(
      worksheet,
      [['Código', 'Cliente', 'Pedido', 'Data Entrada', 'Conta', 'Status']],
      { origin: 'A1' },
    )

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados')

    XLSX.writeFile(workbook, 'tabela.xlsx')
  }

  async function printReportOperationErrors(reclamacao) {
    const dateFormatted = formatDate(new Date(), 'dd/MM/yyyy HH:mm:ss')

    const C = {
      dark: '#111318',
      red: '#C8102E',
      mid: '#5A5F6E',
      light: '#9398A6',
      rule: '#E2E5ED',
      rowAlt: '#F7F8FB',
      white: '#FFFFFF',
    }

    const total = formatPrice(
      (reclamacao.taxa_envio || 0) +
        (reclamacao.taxa_retorno || 0) +
        (reclamacao.debito_ml || 0),
    )

    const cleanLayout = {
      hLineWidth: (i, node) =>
        i === 0 || i === node.table.body.length ? 0 : 0.5,
      vLineWidth: () => 0,
      hLineColor: () => C.rule,
      fillColor: (row) =>
        row === 0 ? C.dark : row % 2 === 0 ? C.rowAlt : C.white,
      paddingLeft: () => 10,
      paddingRight: () => 10,
      paddingTop: () => 7,
      paddingBottom: () => 7,
    }

    const th = (text) => ({ text, fontSize: 8.5, bold: true, color: C.white })
    const td = (text, bold = false, color = C.dark) => ({
      text: String(text ?? '—'),
      fontSize: 9,
      bold,
      color,
    })

    const divider = {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 499,
          y2: 0,
          lineWidth: 0.5,
          lineColor: C.rule,
        },
      ],
      margin: [0, 0, 0, 10],
    }

    const sectionTitle = (label) => ({
      columns: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 3, h: 12, r: 1, color: C.red },
          ],
          width: 10,
          margin: [0, 1, 0, 0],
        },
        {
          text: label.toUpperCase(),
          fontSize: 8,
          bold: true,
          color: C.red,
          letterSpacing: 1.5,
        },
      ],
      margin: [0, 0, 0, 6],
    })

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [48, 72, 48, 52],

      header: (_page, pageCount) => ({
        margin: [48, 16, 48, 0],
        columns: [
          { text: dateFormatted, fontSize: 8, color: C.light },
          {
            text: `Página ${_page} de ${pageCount}`,
            fontSize: 8,
            color: C.light,
            alignment: 'right',
          },
        ],
      }),

      footer: () => ({
        margin: [48, 8, 48, 0],
        stack: [
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 499,
                y2: 0,
                lineWidth: 0.5,
                lineColor: C.rule,
              },
            ],
            margin: [0, 0, 0, 5],
          },
          {
            text: 'Documento de uso interno · gerado automaticamente',
            fontSize: 7.5,
            color: C.light,
            alignment: 'center',
          },
        ],
      }),

      content: [
        // Título
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  stack: [
                    {
                      text: 'RELATÓRIO DE',
                      fontSize: 8,
                      bold: false,
                      color: '#9398A6',
                      letterSpacing: 3,
                      margin: [0, 0, 0, 3],
                    },
                    {
                      text: 'Erros de Operação',
                      fontSize: 22,
                      bold: true,
                      color: C.white,
                    },
                    {
                      text: `Agente: ${reclamacao?.agente || 'Não Identificado'}`,
                      fontSize: 9,
                      color: '#C8102E',
                      bold: true,
                      margin: [0, 6, 0, 0],
                    },
                  ],
                  fillColor: C.dark,
                  margin: [20, 16, 20, 16],
                },
              ],
            ],
          },
          layout: 'noBorders',
          margin: [0, 0, 0, 28],
        },

        // Ocorrência
        sectionTitle('Ocorrência'),
        divider,
        {
          table: {
            headerRows: 1,
            widths: ['auto', '*', 'auto', 'auto', 'auto'],
            body: [
              [
                th('Código'),
                th('Ocorrência'),
                th('Pedido'),
                th('Conta'),
                th('Data'),
              ],
              [
                td(reclamacao.codigo),
                td(reclamacao.motivo_reclamacao),
                td(reclamacao.numero_pedido),
                td(reclamacao.id_loja),
                td(formatDate(reclamacao.data_entrada)),
              ],
            ],
          },
          layout: cleanLayout,
          margin: [0, 0, 0, 24],
        },

        // Débitos
        sectionTitle('Débitos'),
        divider,
        {
          table: {
            headerRows: 1,
            widths: ['*', '*', '*', '*'],
            body: [
              [
                th('Tx. Envio'),
                th('Tx. Retorno'),
                th('Tx. Débito ML'),
                th('Total'),
              ],
              [
                td(formatPrice(reclamacao.taxa_envio)),
                td(formatPrice(reclamacao.taxa_retorno)),
                td(formatPrice(reclamacao.debito_ml)),
                td(total, true, C.red),
              ],
            ],
          },
          layout: cleanLayout,
          margin: [0, 0, 0, 24],
        },

        // Problema Ocasionado
        sectionTitle('Problema Ocasionado'),
        divider,
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  text: reclamacao?.problema_ocasionado || 'Não Identificado',
                  fontSize: 9.5,
                  color: C.dark,
                  lineHeight: 1.5,
                  fillColor: '#FBF0F2',
                  margin: [14, 12, 14, 12],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0,
            vLineWidth: (i) => (i === 0 ? 2 : 0),
            vLineColor: () => C.red,
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0,
          },
          margin: [0, 0, 0, 48],
        },

        // Assinatura
        {
          stack: [
            {
              canvas: [
                {
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 180,
                  y2: 0,
                  lineWidth: 1,
                  lineColor: C.dark,
                },
              ],
              margin: [0, 0, 0, 6],
            },
            {
              text: reclamacao?.agente || 'Não Identificado',
              fontSize: 9.5,
              bold: true,
              color: C.dark,
            },
            {
              text: 'Assinatura do Agente',
              fontSize: 8,
              color: C.light,
              margin: [0, 2, 0, 0],
            },
          ],
        },
      ],

      defaultStyle: {
        font: 'Roboto',
        fontSize: 9,
        color: C.dark,
      },
    }

    pdfMake.createPdf(docDefinition).open()
  }

  async function handleSyncReclamacoes() {
    try {
      setIsSyncLoading(true)

      await ReclamacoesService.sync()

      toast.success('Sincronização concluída com sucesso!')
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível concluir a sincronização!')
    } finally {
      setIsSyncLoading(false)
    }
  }

  useEffect(() => {
    loadReclamacoes()
    loadStatus()
  }, [])

  return (
    <>
      <Navbar />

      <div className="mt-page">
        <h1>Reclamações</h1>

        <Row>
          <h5>Filtros</h5>

          <Col xs={2}>
            <Form.Label>Paginação:</Form.Label>
            <Form.Select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value))
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </Col>

          <Col>
            <Form.Label>Cliente:</Form.Label>
            <Form.Control
              value={cliente}
              onChange={(e) => {
                setCliente(e.target.value)
              }}
            />
          </Col>

          <Col xs={2}>
            <Form.Label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              Status:
              {isStatusLoading && <CustomSpinner size={20} />}
            </Form.Label>

            <Form.Select
              disabled={isStatusLoading}
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value)
              }}
            >
              <option value="">
                {isStatusLoading ? 'Carregando...' : 'Todos'}
              </option>

              {status.map((st) => (
                <option
                  key={Math.random()}
                  value={st}
                >
                  {st}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col xs={2}>
            <Form.Label>Contas</Form.Label>

            <Form.Select
              value={selectedAccount}
              onChange={(e) => {
                setSelectedAccount(e.target.value)
              }}
            >
              <option value="">Todas</option>

              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option value={`ML${String(n)}`}>{`ML${String(n)}`}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <hr />

        <Row>
          <h5>Pesquisa</h5>

          <Col xs={2}>
            <Form.Label>De</Form.Label>

            <CustomInputDate
              selected={startDate}
              onChange={setStartDate}
            />
          </Col>

          <Col xs={2}>
            <Form.Label>Até</Form.Label>

            <CustomInputDate
              selected={endDate}
              onChange={setEndDate}
            />
          </Col>

          <Col
            xs={2}
            style={{
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            <CustomButton
              title="Pesquisar"
              isDisabled={!startDate || !endDate}
              noMargin
              onClick={() => loadReclamacoes(startDate, endDate)}
              isLoading={isRequestLoading}
              isLoadingText="Pesquisando..."
            >
              <Search />
            </CustomButton>
          </Col>
        </Row>

        <hr />

        <Row>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
            }}
          >
            <CustomButton
              color="#05a416"
              title="Novo"
              onClick={() => setIsModalCreateOpen(true)}
            >
              <PlusCircleFill />
            </CustomButton>

            <CustomButton
              color="#3f0cc5"
              title="Sincronizar"
              onClick={handleSyncReclamacoes}
              isLoading={isSyncLoading}
              isLoadingText="Sincronizando, aguarde..."
            >
              <ArrowRepeat />
            </CustomButton>

            <CustomButton
              color="#275DAD"
              title="Atualizar"
              onClick={loadReclamacoes}
              isLoading={isRequestLoading}
              isLoadingText="Atualizando..."
            >
              <ArrowRepeat />
            </CustomButton>

            <CustomButton
              color="#087640"
              title="Exportar para o Excel"
              onClick={exportToExcel}
            >
              <FiletypeXlsx />
            </CustomButton>
          </div>
        </Row>

        <ContentLoading
          isLoading={isRequestLoading}
          loadingText="Carregando..."
        >
          <Table>
            <thead>
              <tr>
                <Th
                  label="ID"
                  value="codigo"
                />

                <Th
                  label="Cliente"
                  value="cliente"
                />

                <Th
                  label="Pedido"
                  value="numero_pedido"
                />

                <Th
                  label="Data Entrada"
                  value="data_entrada"
                />

                <Th
                  label="Conta"
                  value="apelido"
                />

                <Th
                  label="Status"
                  value="status"
                />

                <Th
                  label="Tipo Reclamação"
                  value="motivo_reclamacao"
                />

                <Th label="Ações" />
              </tr>
            </thead>

            <tbody>
              {filteredItems.map((reclamacao, index) => (
                <>
                  <tr key={reclamacao.codigo}>
                    <td>{reclamacao.codigo}</td>
                    <td>{reclamacao.cliente}</td>
                    <td>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <span>{reclamacao.numero_pedido}</span>

                        <OverlayTrigger
                          overlay={<Tooltip>Ir até pedido</Tooltip>}
                        >
                          <CustomButton
                            color="#9B287B"
                            onClick={() => {
                              navigate(
                                `/pedido/editar/${reclamacao.numero_pedido}`,
                              )
                            }}
                          >
                            <BoxArrowUpRight />
                          </CustomButton>
                        </OverlayTrigger>
                      </div>
                    </td>
                    <td>{formatDate(reclamacao.data_entrada)}</td>
                    <td>{reclamacao.apelido}</td>
                    <td>{reclamacao.status}</td>
                    <td>{reclamacao.motivo_reclamacao}</td>
                    <td
                      style={{
                        display: 'flex',
                      }}
                    >
                      <OverlayTrigger
                        overlay={<Tooltip>Mostrar Ações</Tooltip>}
                      >
                        <CustomButton
                          color="#2D936C"
                          onClick={() =>
                            isMoreActionsOpen[index]
                              ? setIsMoreActionsOpen({})
                              : setIsMoreActionsOpen({
                                  [index]: true,
                                })
                          }
                        >
                          <PlusCircleFill />
                        </CustomButton>
                      </OverlayTrigger>
                    </td>
                  </tr>

                  {isMoreActionsOpen[index] && (
                    <tr>
                      <td colSpan={7}>
                        <div
                          style={{
                            minWidth: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ButtonGroup size="sm">
                            <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
                              <CustomButton
                                onClick={() => {
                                  setSelectedClaimCode(reclamacao.codigo)
                                  setIsModalEditOpen(true)
                                }}
                                isEdit
                              >
                                <PencilFill />
                              </CustomButton>
                            </OverlayTrigger>

                            <OverlayTrigger
                              overlay={<Tooltip>Excluir</Tooltip>}
                            >
                              <CustomButton
                                isDelete
                                isLoading={isDeleteRequestLoading[index]}
                                onClick={() =>
                                  handleAskDelete(reclamacao.codigo, index)
                                }
                              >
                                <TrashFill />
                              </CustomButton>
                            </OverlayTrigger>

                            <OverlayTrigger
                              overlay={<Tooltip>Imprimir Relatório</Tooltip>}
                            >
                              <CustomButton
                                color="#1B998B"
                                isLoading={isDeleteRequestLoading[index]}
                                onClick={() =>
                                  printReportOperationErrors(reclamacao)
                                }
                              >
                                <PrinterFill />
                              </CustomButton>
                            </OverlayTrigger>
                          </ButtonGroup>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </Table>

          <Paginate />
        </ContentLoading>
      </div>

      {isModalCreateOpen && (
        <ModalCreate
          isOpen={isModalCreateOpen}
          loadReclamacoes={loadReclamacoes}
          onClose={() => setIsModalCreateOpen(false)}
        />
      )}

      {isModalEditOpen && (
        <ModalEdit
          isOpen={isModalEditOpen}
          onClose={() => setIsModalEditOpen(false)}
          code={selectedClaimCode}
          loadReclamacoes={loadReclamacoes}
        />
      )}
    </>
  )
}
